import React, { useMemo, useState } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";

import {
  ADMIN_HERO_SECTION_QUERY,
  UPSERT_HERO_SECTION_MUTATION,
} from "../../graphql/heroSection";

import AdminLoading from "../Components/AdminLoading";
import HeroSectionFormDialog, {
  HeroSectionForm,
} from "../Components/HeroSectionFormDialog";

const HeroSectionAdminPage: React.FC = () => {
  const { data, loading, error } = useQuery(ADMIN_HERO_SECTION_QUERY);

  const [upsert, { loading: saving }] = useMutation(
    UPSERT_HERO_SECTION_MUTATION,
    {
      refetchQueries: [{ query: ADMIN_HERO_SECTION_QUERY }],
    },
  );

  const hero: HeroSectionForm = useMemo(() => {
    const h = data?.adminHeroSection;
    if (!h) {
      return {
        id: "",
        heading: "",
        subheading: "",
        description: "",
        leftImage: "",
        rightImage: "",
      };
    }

    return {
      id: h.id || "",
      heading: h.heading || "",
      subheading: h.subheading || "",
      description: h.description || "",
      leftImage: h.leftImage || "",
      rightImage: h.rightImage || "",
    };
  }, [data]);

  const [open, setOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const save = async (form: HeroSectionForm) => {
    setActionError(null);

    try {
      const res = await upsert({
        variables: {
          heading: form.heading || null,
          subheading: form.subheading || null,
          description: form.description || null,
          leftImage: form.leftImage || null,
          rightImage: form.rightImage || null,
        },
      });

      const payload = res.data?.adminUpsertHeroSection;
      const errors: string[] = payload?.errors || [];

      if (errors.length) {
        setActionError(errors.join(", "));
        return;
      }

      setOpen(false);
    } catch (e: any) {
      setActionError(e?.message || "Failed to save hero section.");
    }
  };

  return (
    <Box>
      <AdminLoading open={loading || saving} />

      <Typography variant="h5" mb={2}>
        Hero Section
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Images should be <strong>portrait</strong> 1600x2000 size to be
        displayed on hero page. Text appears in center column.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      <Button variant="contained" onClick={() => setOpen(true)}>
        Edit Hero Section
      </Button>

      <HeroSectionFormDialog
        open={open}
        hero={open ? hero : null}
        onClose={() => setOpen(false)}
        onSave={save}
      />
    </Box>
  );
};

export default HeroSectionAdminPage;
